import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  GraduationCap, Users, ArrowRight, ArrowLeft, Check, CalendarPlus, Share2, Loader2,
} from "lucide-react";
import Select from "@/components/Form/Select/Select";
import applicationsService from "@/services/applications/applicationsService";

interface FormValues {
  applicant_type: "student" | "parent" | "";
  student_name: string;
  parent_name: string;
  email: string;
  applied_cohort_id: string;
  countryCode: string;
  contact_number: string;
  country: string;
  state: string;
  city: string;
  school_name: string;
  school_type: "Public" | "Private" | "";
  grade: string;
  theme_ids: string[];
  programming_experience: number;
  accomplishments: string;
  discovery_source: string;
  referral_name?: string;
  referral_code: string;
}

export default function ApplicationForm() {
  const { t } = useTranslation("landing");
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const total = 5;
  const progress = useMemo(() => ((step - 1) / total) * 100, [step]);

  // React Query calls
  const { data: countriesRes } = useQuery({
    queryKey: ["countriesStates"],
    queryFn: () => applicationsService.getCountriesStates(),
  });
  const countries = countriesRes?.data || [];

  const { data: themesRes } = useQuery({
    queryKey: ["themes"],
    queryFn: () => applicationsService.getThemes(),
  });
  const dbThemes = themesRes?.data || [];

  const { data: cohortsRes } = useQuery({
    queryKey: ["openCohorts"],
    queryFn: () => applicationsService.getOpenCohorts(),
  });
  const cohorts = cohortsRes?.data || [];

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm<FormValues>({
    defaultValues: {
      applicant_type: "",
      student_name: "",
      referral_code:"",
      parent_name: "",
      email: "",
      applied_cohort_id: "",
      countryCode: "+91",
      contact_number: "",
      country: "",
      state: "",
      city: "",
      school_name: "",
      school_type: "",
      grade: "",
      theme_ids: [],
      programming_experience: 0,
      accomplishments: "",
      discovery_source: "",
    },
    mode: "onChange",
  });

  const applicantType = watch("applicant_type");
  const selectedCountryName = watch("country");
  const appliedCohortId = watch("applied_cohort_id");
  const emailValue = watch("email");
  const discoverySource = watch("discovery_source");

  const studentNameVal = watch("student_name");
  const parentNameVal = watch("parent_name");
  const contactNumberVal = watch("contact_number");
  const cityVal = watch("city");
  const stateVal = watch("state");
  const schoolNameVal = watch("school_name");
  const schoolTypeVal = watch("school_type");
  const gradeVal = watch("grade");
  const themeIdsVal = watch("theme_ids") || [];
  const programmingExperienceVal = watch("programming_experience");
  const accomplishmentsVal = watch("accomplishments") || "";


  const canNext = () => {
    switch (step) {
      case 1:
        return !!applicantType;
      case 2:
        return !!studentNameVal && !!parentNameVal && !!emailValue && !!appliedCohortId;
      case 3:
        return !!contactNumberVal && !!cityVal && !!stateVal && !!selectedCountryName && !!schoolNameVal && !!schoolTypeVal && !!gradeVal;
      case 4:
        return themeIdsVal.length > 0 && programmingExperienceVal > 0 && accomplishmentsVal.trim().length >= 5;
      case 5:
        return !!discoverySource;
    }
    return false;
  };

  const handleReferralCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stripped = e.target.value.replace(/\s+/g, "");
    if (stripped !== e.target.value) {
      setValue("referral_code", stripped, { shouldDirty: true });
    }
    clearErrors("referral_code");
  };

  const handleContactNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 11);
    if (digitsOnly !== e.target.value) {
      setValue("contact_number", digitsOnly, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleContinue = async () => {
    let fieldsToValidate: any[] = [];
    if (step === 1) {
      fieldsToValidate = ["applicant_type"];
    } else if (step === 2) {
      fieldsToValidate = ["student_name", "parent_name", "email", "applied_cohort_id"];
    } else if (step === 3) {
      fieldsToValidate = ["country", "state", "city", "school_name", "school_type", "grade", "contact_number"];
    } else if (step === 4) {
      fieldsToValidate = ["theme_ids", "programming_experience", "accomplishments"];
    }

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    if (step === 2) {
      setIsCheckingEmail(true);
      try {
        const checkRes = await applicationsService.validateEmail(emailValue, appliedCohortId);
        if (!checkRes.data?.isAvailable) {
          setError("email", {
            type: "manual",
            message: t("applicationForm.emailValidation"),
          });
          return;
        }
        clearErrors("email");
        setStep(3);
      } catch (err) {
        setError("email", {
          type: "manual",
          message: "Unable to validate email. Please try again.",
        });
      } finally {
        setIsCheckingEmail(false);
      }
    } else {
      setStep((s) => Math.min(total, s + 1));
    }
  };

  const onSubmit = async (values: FormValues) => {
    const selectedCountryObj = countries.find((c) => c.name === values.country);
    const code = selectedCountryObj?.phoneCode || values.countryCode;
    const fullContactNumber = `${code} ${values.contact_number}`.trim();

    const payload = {
      applicant_type: values.applicant_type,
      student_name: values.student_name,
      parent_name: values.parent_name,
      email: values.email,
      contact_number: fullContactNumber,
      city: values.city,
      state: values.state,
      country: values.country,
      school_name: values.school_name,
      school_type: values.school_type,
      grade: values.grade,
      programming_experience: Number(values.programming_experience),
      accomplishments: values.accomplishments,
      theme_ids: values.theme_ids,
      applied_cohort_id: values.applied_cohort_id,
      discovery_source: values.discovery_source,
      referral_code: values.referral_code,
      referral_name: values.referral_name || undefined,
    };

    setIsSubmitting(true);
    try {
      const res = await applicationsService.submitApplication(payload);
      if (!res.error) {
        setDone(true);
      } else {
        toast.error(res.message || "Failed to submit application");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "An error occurred during submission");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCohort = cohorts.find((c) => c.id === appliedCohortId);
  const cohortTitle = selectedCohort
    ? (selectedCohort.generated_cohort_title || selectedCohort.cohort_name)
    : "Selected Cohort";
  const firstName = (studentNameVal || parentNameVal || "Friend").split(" ")[0];

  const cohortOptions = useMemo(() => {
    return cohorts.map((c) => ({
      value: c.id,
      label: c.cohort_name || c.generated_cohort_title,
    }));
  }, [cohorts]);

  const countryOptions = useMemo(() => {
    return countries.map((c) => ({
      value: c.name,
      label: `${c.flag} ${c.name} (${c.phoneCode})`,
    }));
  }, [countries]);

  const countryCode = watch("countryCode") || "+91";

  const stateOptions = useMemo(() => {
    if (!selectedCountryName) return [];
    const matchedCountry = countries.find((c) => c.name === selectedCountryName);
    if (!matchedCountry) return [];
    return matchedCountry.states.map((s) => ({ value: s, label: s }));
  }, [selectedCountryName, countries]);

  const customSelectStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      borderRadius: '0.75rem', // rounded-xl
      minHeight: '46px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#2A65F5' : '#E2E8F0',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(42, 101, 245, 0.1)' : 'none',
      backgroundColor: 'white',
      padding: '0 2px',
      '&:hover': {
        borderColor: state.isFocused ? '#2A65F5' : '#CBD5E1',
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#94A3B8', // slate-400
      fontSize: '14px',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#0F172A', // navy
      fontSize: '14px',
    }),
  }), []);

  return (
    <section id="apply" className="bg-blue-bg py-24">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="label-eyebrow">{t("applicationForm.eyebrow")}</p>
          <h2 className="mt-3 text-4xl font-bold text-navy sm:text-[42px]">
            {t("applicationForm.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-body">
            {t("applicationForm.subtitle")}
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-3xl bg-white shadow-card ring-1 ring-border">
          {!done && (
            <div className="border-b border-border bg-white px-6 py-4 sm:px-10 rounded-t-3xl">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-body">
                <span>{t("applicationForm.step", { step, total }).replace("{{step}}", String(step)).replace("{{total}}", String(total))}</span>
                <span>{t("applicationForm.complete", { percent: Math.round(progress) }).replace("{{percent}}", String(Math.round(progress)))}</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface">
                <motion.div
                  initial={false}
                  animate={{ width: `${((step) / total) * 100}%` }}
                  transition={{ type: "spring", stiffness: 90, damping: 18 }}
                  className="h-full rounded-full bg-primary"
                />
              </div>
            </div>
          )}

          <div className="p-6 sm:p-10">
            <form onSubmit={handleSubmit(onSubmit)}>
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-8 text-center"
                  >
                    <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-50">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                        className="grid h-14 w-14 place-items-center rounded-full bg-emerald-500"
                      >
                        <Check className="h-7 w-7 text-white" strokeWidth={3} />
                      </motion.div>
                    </div>
                    <h3 className="mt-6 text-3xl font-bold text-navy">{t("applicationForm.doneTitle")}</h3>
                    <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-body">
                      {t("applicationForm.doneBody").replace("{{name}}", firstName).replace("{{batch}}", cohortTitle)}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                      <button type="button" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-accent cursor-pointer">
                        <CalendarPlus className="h-4 w-4" /> {t("applicationForm.addToCalendar")}
                      </button>
                      <button type="button" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-accent cursor-pointer">
                        <Share2 className="h-4 w-4" /> {t("applicationForm.shareWithFriend")}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25 }}
                  >
                    {step === 1 && (
                      <div>
                        <h3 className="text-center text-2xl font-bold text-navy sm:text-3xl">
                          {t("applicationForm.roleQuestion")}
                        </h3>
                        <Controller
                          name="applicant_type"
                          control={control}
                          rules={{ required: "Role selection is required" }}
                          render={({ field }) => (
                            <div className="mt-8 grid gap-4 sm:grid-cols-2">
                              <RoleCard
                                icon={<GraduationCap className="h-8 w-8" />}
                                label={t("applicationForm.roleStudent")}
                                active={field.value === "student"}
                                onClick={() => field.onChange("student")}
                              />
                              <RoleCard
                                icon={<Users className="h-8 w-8" />}
                                label={t("applicationForm.roleParent")}
                                active={field.value === "parent"}
                                onClick={() => field.onChange("parent")}
                              />
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-5">
                        <h3 className="text-2xl font-bold text-navy">{t("applicationForm.basicDetails")}</h3>
                        <Field label={t("applicationForm.studentName")} required error={errors.student_name?.message}>
                          <input className={inputCls} {...register("student_name", { required: "Student Name is required" })} placeholder="Please Enter the Student Name" />
                        </Field>
                        <Field label={t("applicationForm.parentName")} required error={errors.parent_name?.message}>
                          <input className={inputCls} {...register("parent_name", { required: "Parent/Guardian Name is required" })} placeholder="Please Enter the Parent/Guardian Name" />
                        </Field>
                        <Field label={t("applicationForm.email")} required error={errors.email?.message}>
                          <input
                            type="email"
                            placeholder={t("applicationForm.emailPlaceholder")}
                            className={inputCls}
                            {...register("email", {
                              required: "Email Address is required",
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t("applicationForm.emailInvalid"),
                              },
                            })}
                          />
                        </Field>
                        <Field label={t("applicationForm.referralCode")} error={errors.referral_code?.message}>
                          <input
                            className={inputCls}
                            {...register("referral_code", { onChange: handleReferralCodeChange })}
                            placeholder="please enter referral code"
                          />
                        </Field>
                        <Controller
                          name="applied_cohort_id"
                          control={control}
                          rules={{ required: "Cohort is required" }}
                          render={({ field }) => (
                            <div className="flex flex-col gap-2">
                              {/* Label */}
                              <label className="text-base font-semibold text-gray-900">
                                {t("applicationForm.cohortName")} <span className="text-red-500">*</span>
                              </label>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                {cohortOptions.length > 0 ? (
                                  cohortOptions.map((option) => {
                                    const isSelected = field.value === option.value;

                                    return (
                                      <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => {
                                          field.onChange(option.value);
                                          clearErrors("email");
                                        }}
                                        className={`flex items-center gap-3 p-4 rounded-3xl border-2 text-left transition-all font-medium w-full
                                        ${isSelected
                                            ? "border-blue-600 bg-blue-50 text-blue-900 shadow-sm"
                                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                                          }
                                        `}
                                      >
                                        {/* Calendar Icon - prevents squishing */}
                                        <svg
                                          className={`w-5 h-5 shrink-0 ${isSelected ? "text-blue-600" : "text-gray-400"}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                          />
                                        </svg>

                                        {/* Text: strictly one line, shows everything */}
                                        <span className="text-base font-semibold">
                                          {option.label}
                                        </span>
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="rounded-2xl bg-amber-50 p-4 border border-amber-200 text-amber-800 text-sm font-medium w-full flex items-center gap-3">
                                    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    There are currently no open cohorts for application. Please check back later.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-5">
                        <h3 className="text-2xl font-bold text-navy">{t("applicationForm.schoolContact")}</h3>
                        <Controller
                          name="country"
                          control={control}
                          rules={{ required: "Country is required" }}
                          render={({ field, fieldState: { error } }) => (
                            <Select
                              label={t("applicationForm.country")}
                              required
                              placeholder={t("applicationForm.countryPlaceholder")}
                              options={countryOptions}
                              value={field.value}
                              virtualizeMenuList
                              styles={customSelectStyles}
                              onChange={(val) => {
                                field.onChange(val);
                                setValue("state", "");
                                const matchedCountry = countries.find((c) => c.name === val);
                                if (matchedCountry) {
                                  setValue("countryCode", matchedCountry.phoneCode);
                                }
                              }}
                              error={error?.message}
                            />
                          )}
                        />
                        <Field label={t("applicationForm.parentPhone")} required error={errors.contact_number?.message}>
                          <div className="flex gap-2">
                            <div className="flex items-center justify-center rounded-xl border border-border bg-slate-50 px-4 py-3 text-sm text-navy font-semibold min-w-[80px]">
                              {countryCode}
                            </div>
                            <input
                              className={inputCls}
                              placeholder="98765 43210"
                              inputMode="numeric"
                              maxLength={11}
                              {...register("contact_number", {
                                required: "Phone number is required",
                                onChange: handleContactNumberChange,
                                pattern: {
                                  value: /^[0-9]{7,11}$/,
                                  message: "Enter a valid phone number (7 to 11 digits)",
                                },
                              })}
                            />
                          </div>
                        </Field>
                        <div className="grid gap-5 sm:grid-cols-2">
                          <Field label={t("applicationForm.city")} required error={errors.city?.message}>
                            <input className={inputCls} {...register("city", { required: "City is required" })} placeholder="Please Enter the City" />
                          </Field>
                          <Controller
                            name="state"
                            control={control}
                            rules={{ required: "State / Region is required" }}
                            render={({ field, fieldState: { error } }) => (
                              stateOptions.length > 0 ? (
                                <Select
                                  label={t("applicationForm.state")}
                                  required
                                  placeholder={t("applicationForm.statePlaceholder")}
                                  options={stateOptions}
                                  value={field.value}
                                  styles={customSelectStyles}
                                  onChange={field.onChange}
                                  isDisabled={!selectedCountryName}
                                  error={error?.message}
                                />
                              ) : (
                                <Field
                                  label={t("applicationForm.state")}
                                  required
                                  error={error?.message}
                                >
                                  <input
                                    className={inputCls}
                                    placeholder={selectedCountryName ? "Enter your state / region / province" : "Select a country first"}
                                    disabled={!selectedCountryName}
                                    {...field}
                                  />
                                </Field>
                              )
                            )}
                          />
                        </div>
                        <Field label={t("applicationForm.schoolName")} required error={errors.school_name?.message}>
                          <input className={inputCls} {...register("school_name", { required: "School name is required" })} placeholder="Please Enter the School Name" />
                        </Field>
                        <div className="grid gap-5 sm:grid-cols-2">
                          <Field label={t("applicationForm.schoolType")} required error={errors.school_type?.message}>
                            <Controller
                              name="school_type"
                              control={control}
                              rules={{ required: "School type is required" }}
                              render={({ field }) => (
                                <div className="inline-flex rounded-full bg-surface p-1 mt-1">
                                  {(["Public", "Private"] as const).map((tType) => (
                                    <button
                                      type="button"
                                      key={tType}
                                      onClick={() => field.onChange(tType)}
                                      className={`rounded-full px-5 py-2 text-sm font-semibold transition-all cursor-pointer ${field.value === tType ? "bg-primary text-white" : "text-slate-body bg-transparent border-0"
                                        }`}
                                    >
                                      {tType}
                                    </button>
                                  ))}
                                </div>
                              )}
                            />
                          </Field>
                          <Controller
                            name="grade"
                            control={control}
                            rules={{ required: "Grade is required" }}
                            render={({ field, fieldState: { error } }) => {
                              const labelText = applicantType === "parent" ? t("applicationForm.childGrade") : t("applicationForm.grade");
                              const gradeOptions = [8, 9, 10, 11, 12].map((g) => ({
                                value: `Grade ${g}`,
                                label: `Grade ${g}`,
                              }));
                              return (
                                <Select
                                  label={labelText}
                                  required
                                  placeholder={t("applicationForm.gradePlaceholder")}
                                  options={gradeOptions}
                                  value={field.value}
                                  styles={customSelectStyles}
                                  onChange={field.onChange}
                                  error={error?.message}
                                />
                              );
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {step === 4 && (
                      <div className="space-y-7">
                        <div>
                          <h3 className="text-2xl font-bold text-navy">{t("applicationForm.academicProfile")}</h3>
                          <p className="mt-2 text-sm text-slate-body">
                            {t("applicationForm.academicProfileSub")}
                          </p>
                        </div>

                        <Field
                          label={applicantType === "parent" ? t("applicationForm.interestQuestionParent") : t("applicationForm.interestQuestion")}
                          required
                          error={errors.theme_ids?.message}
                        >
                          <Controller
                            name="theme_ids"
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (!value || value.length === 0) return "At least one interest domain must be selected";
                                if (value.length > 3) return "You can select up to 3 interest domains";
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <div className="flex flex-wrap gap-2">
                                {dbThemes.map((theme) => {
                                  const on = field.value?.includes(theme.id);
                                  return (
                                    <button
                                      type="button"
                                      key={theme.id}
                                      onClick={() => {
                                        if (on) {
                                          field.onChange(field.value.filter((x) => x !== theme.id));
                                          clearErrors("theme_ids");
                                        } else if ((field.value || []).length < 3) {
                                          field.onChange([...(field.value || []), theme.id]);
                                          clearErrors("theme_ids");
                                        } else {
                                          setError("theme_ids", {
                                            type: "manual",
                                            message: "You can select up to 3 interest domains",
                                          });
                                        }
                                      }}
                                      className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold transition-all cursor-pointer ${on ? "border-primary bg-primary text-white" : "border-border bg-white text-slate-body hover:border-primary/40"
                                        }`}
                                    >
                                      {theme.theme_name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          />
                        </Field>

                        <Field label={t("applicationForm.experienceQuestion")} required error={errors.programming_experience?.message}>
                          <Controller
                            name="programming_experience"
                            control={control}
                            rules={{
                              required: "Experience level rating is required",
                              min: { value: 1, message: "Please select experience level" },
                            }}
                            render={({ field }) => (
                              <div className="rounded-2xl bg-surface p-5">
                                <div className="h-2 rounded-full bg-gradient-to-r from-amber-300 via-lime-400 to-emerald-500" />
                                <div className="mt-4 grid grid-cols-5 gap-2">
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                      type="button"
                                      key={n}
                                      onClick={() => field.onChange(n)}
                                      className={`rounded-xl py-3 text-base font-bold transition-all cursor-pointer ${field.value === n
                                        ? "bg-primary text-white shadow-glow border-0"
                                        : "bg-white text-navy ring-1 ring-border hover:ring-primary/40 border-0"
                                        }`}
                                    >
                                      {n}
                                    </button>
                                  ))}
                                </div>
                                <div className="mt-3 grid grid-cols-5 gap-2 text-[10px] font-medium text-slate-body sm:text-xs">
                                  <span className="text-center">{t("applicationForm.expNew")}</span>
                                  <span className="text-center">{t("applicationForm.expBasic")}</span>
                                  <span className="text-center">{t("applicationForm.expProj")}</span>
                                  <span className="text-center">{t("applicationForm.expInter")}</span>
                                  <span className="text-center">{t("applicationForm.expStrong")}</span>
                                </div>
                                <p className="mt-4 text-xs italic text-slate-body">
                                  {t("applicationForm.experienceSub")}
                                </p>
                              </div>
                            )}
                          />
                        </Field>

                        <Field
                          label={t("applicationForm.accomplishments")}
                          sub={t("applicationForm.accomplishmentsSub")}
                          required
                          error={errors.accomplishments?.message}
                        >
                          <textarea
                            rows={5}
                            className={inputCls}
                            placeholder="e.g. Led a team project on social issues, received distinction in International Math Assessment..."
                            {...register("accomplishments", {
                              required: "Activities and accomplishments share is required",
                              minLength: { value: 5, message: "Accomplishments description must be at least 5 characters" },
                            })}
                          />
                        </Field>

                        <div className="rounded-2xl bg-blue-bg p-5 text-sm font-semibold text-navy">
                          {t("applicationForm.almostThere")}
                        </div>

                        {/* <div className="flex flex-col items-start justify-between gap-4 rounded-2xl bg-navy p-6 text-white sm:flex-row sm:items-center">
                          <div>
                            <div className="font-bold">{t("applicationForm.questionsTitle")}</div>
                            <div className="mt-1 text-sm text-white/70">{t("applicationForm.questionsSub")}</div>
                          </div>
                          <a href="mailto:admissions@thepod.academy" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-navy no-underline">
                            <Phone className="h-4 w-4" /> {t("applicationForm.bookCall")}
                          </a>
                        </div> */}
                      </div>
                    )}

                    {step === 5 && (
                      <div className="space-y-5">
                        <h3 className="text-2xl font-bold text-navy">{t("applicationForm.discoveryTitle")}</h3>

                        <Controller
                          name="discovery_source"
                          control={control}
                          rules={{ required: "Discovery source is required" }}
                          render={({ field, fieldState: { error } }) => {
                            const sourceOptions = ["Social Media", "Google Search", "Friend / Family / Alumni", "School / Teacher", "YouTube", "Other"].map(o => ({
                              value: o,
                              label: o,
                            }));
                            return (
                              <Select
                                label={t("applicationForm.sourceQuestion")}
                                required
                                placeholder={t("applicationForm.sourcePlaceholder")}
                                options={sourceOptions}
                                value={field.value}
                                styles={customSelectStyles}
                                onChange={field.onChange}
                                error={error?.message}
                              />
                            );
                          }}
                        />




                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {!done && (
                <div className="mt-10 flex items-center justify-between gap-3 border-t border-border pt-6">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    disabled={step === 1 || isCheckingEmail || isSubmitting}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy disabled:opacity-30 cursor-pointer"
                  >
                    <ArrowLeft className="h-4 w-4" /> {t("applicationForm.backButton")}
                  </button>
                  {step < total ? (
                    <button
                      type="button"
                      onClick={handleContinue}
                      disabled={!canNext() || isCheckingEmail || isSubmitting}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white shadow-glow disabled:opacity-40 disabled:shadow-none border-0 cursor-pointer"
                    >
                      {isCheckingEmail ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Checking Email...
                        </>
                      ) : (
                        <>
                          {t("applicationForm.continueButton")} <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!canNext() || isSubmitting}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-bold text-white shadow-glow disabled:opacity-40 sm:w-auto border-0 cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          {t("applicationForm.submitButton")} <ArrowRight className="h-5 w-5" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none transition-all placeholder:text-slate-body/60 focus:border-primary focus:ring-4 focus:ring-primary/10";

function Field({
  label, sub, required, error, children,
}: { label: string; sub?: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {sub && <p className="mt-1 text-xs text-slate-body">{sub}</p>}
      <div className="mt-2">{children}</div>
      {error && (
        <span className="text-xs text-rose-500 mt-1 block" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function RoleCard({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-4 rounded-3xl border-2 p-8 transition-all cursor-pointer ${active
        ? "border-primary bg-blue-bg shadow-glow"
        : "border-border bg-white hover:-translate-y-1 hover:border-primary/40 hover:shadow-soft"
        }`}
    >
      <span className={`grid h-16 w-16 place-items-center rounded-2xl ${active ? "bg-primary text-white" : "bg-blue-bg text-primary"}`}>
        {icon}
      </span>
      <span className="text-lg font-bold text-navy">{label}</span>
    </button>
  );
}
